import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addToast,
  Button,
  DateField,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@structural-climbing/ui'
import { ageFrom } from '../members/ageFrom'
import { useCalendarApi } from './useCalendarApi'
import type { ClassSession } from './types'

interface Props {
  session: ClassSession
  onClose: () => void
  /** Someone was booked: re-read the roster. */
  onBooked: () => void
  /** Book someone already on file (a member, or one of their children). */
  onBookExisting: (userId: string, childId: string | null) => Promise<boolean>
}

/** The member a kid's phone belongs to: most likely the parent calling. */
interface PhoneOwner {
  member: { userId: string; name: string | null; firstName: string | null; lastName: string | null }
  children: {
    id: string
    first_name: string
    last_name: string | null
    birth_date: string | null
    booked: boolean
  }[]
}

const isDay = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v)
const phoneDigits = (v: string) => v.replace(/\D/g, '').length

/**
 * A class booked over the phone by someone who is not a member yet: a name and
 * a phone number, and a date of birth only when the class has an age band. The
 * Worker adds them as a desk member and books them with no plan. The desk
 * completes their details and sells a plan when they come in.
 *
 * A kid's phone is the parent's. Under 18 the form asks the parent's name, and
 * if the phone belongs to a member it fills that in and lists their children,
 * so a kid already on file is booked rather than added twice.
 */
export function PhoneBookingModal({ session, onClose, onBooked, onBookExisting }: Props) {
  const { t } = useTranslation()
  const api = useCalendarApi()
  const needsBirth = session.min_age !== null || session.max_age !== null

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [guardianFirstName, setGuardianFirstName] = useState('')
  const [guardianLastName, setGuardianLastName] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [existing, setExisting] = useState<{ userId: string; name: string | null } | null>(null)
  const [owner, setOwner] = useState<PhoneOwner | null>(null)
  const [saving, setSaving] = useState(false)

  const isMinor = isDay(dateOfBirth) && ageFrom(dateOfBirth) < 18

  // A kid's phone: look up whether the parent is a member already.
  const lookupPhone = isMinor && phoneDigits(phone) >= 9 ? phone.trim() : ''
  useEffect(() => {
    setOwner(null)
    if (!lookupPhone) return
    let stale = false
    const timer = setTimeout(() => {
      void api<PhoneOwner | { member: null }>(
        `/class-sessions/${session.id}/phone-lookup?phone=${encodeURIComponent(lookupPhone)}`,
      ).then((res) => {
        if (stale || !res.ok || !res.data.member) return
        const found = res.data as PhoneOwner
        setOwner(found)
        // The parent's name, unless staff already typed one.
        setGuardianFirstName((v) => v || found.member.firstName || '')
        setGuardianLastName((v) => v || found.member.lastName || '')
      })
    }, 300)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [api, lookupPhone, session.id])

  async function save() {
    const required = t('calendar.phoneBookingRequired')
    const next: Record<string, string> = {}
    if (!firstName.trim()) next.firstName = required
    if (!lastName.trim()) next.lastName = required
    if (!phone.trim()) next.phone = required
    if (needsBirth && !dateOfBirth) next.dateOfBirth = required
    if (isMinor && !guardianFirstName.trim()) next.guardianFirstName = required
    if (isMinor && !guardianLastName.trim()) next.guardianLastName = required
    setErrors(next)
    if (Object.keys(next).length) return

    setSaving(true)
    const res = await api<{ userId?: string; name?: string | null; reason?: string }>(
      `/class-sessions/${session.id}/phone-booking`,
      'POST',
      {
        firstName,
        lastName,
        phone,
        ...(dateOfBirth ? { dateOfBirth } : {}),
        ...(isMinor ? { guardianFirstName, guardianLastName } : {}),
      },
    )
    setSaving(false)
    if (res.ok) {
      addToast({ title: t('calendar.phoneBookingDone'), color: 'success' })
      onBooked()
      return
    }
    const code = res.data.code
    if (code === 'PHONE_EXISTS' && res.data.userId) {
      setExisting({ userId: res.data.userId, name: res.data.name ?? null })
      return
    }
    if (code === 'PHONE_INVALID') {
      setErrors({ phone: t('calendar.phoneBookingPhoneInvalid') })
      return
    }
    if (code === 'DATE_OF_BIRTH_REQUIRED') {
      setErrors({ dateOfBirth: required })
      return
    }
    if (code === 'GUARDIAN_REQUIRED') {
      setErrors({ guardianFirstName: required, guardianLastName: required })
      return
    }
    addToast({
      title:
        code === 'CLASS_FULL'
          ? t('calendar.errorFull')
          : res.data.reason === 'age'
            ? t('calendar.phoneBookingWrongAge')
            : code === 'PAST'
              ? t('calendar.errorPast')
              : res.status === 403
                ? t('calendar.phoneBookingForbidden')
                : t('calendar.errorSave'),
      color: 'danger',
    })
  }

  async function bookOnFile(userId: string, childId: string | null) {
    setSaving(true)
    const booked = await onBookExisting(userId, childId)
    setSaving(false)
    if (booked) onClose()
  }

  return (
    <Modal
      isOpen
      size="lg"
      scrollBehavior="inside"
      isDismissable={!saving}
      onOpenChange={(open) => {
        if (!open && !saving) onClose()
      }}
    >
      <ModalContent>
        <form
          className="flex min-h-0 flex-col"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <ModalHeader className="flex flex-col gap-1">
            {t('calendar.phoneBookingTitle')}
            <span className="text-sm font-normal text-foreground/60">
              {t('calendar.phoneBookingIntro')}
            </span>
          </ModalHeader>
          <ModalBody>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('members.detailsFirstName')}
                value={firstName}
                onValueChange={setFirstName}
                maxLength={100}
                isInvalid={!!errors.firstName}
                errorMessage={errors.firstName}
              />
              <Input
                label={t('members.detailsLastName')}
                value={lastName}
                onValueChange={setLastName}
                maxLength={100}
                isInvalid={!!errors.lastName}
                errorMessage={errors.lastName}
              />
              <Input
                type="tel"
                label={t('members.detailsPhone')}
                description={isMinor ? t('calendar.phoneBookingParentPhoneHint') : undefined}
                value={phone}
                onValueChange={(v) => {
                  setPhone(v)
                  setExisting(null)
                }}
                maxLength={20}
                isInvalid={!!errors.phone}
                errorMessage={errors.phone}
              />
              {needsBirth && (
                <DateField
                  label={t('members.detailsDateOfBirth')}
                  description={t('calendar.phoneBookingBirthHint')}
                  value={dateOfBirth}
                  onValueChange={(v) => {
                    setDateOfBirth(v)
                    setExisting(null)
                  }}
                  isInvalid={!!errors.dateOfBirth}
                  errorMessage={errors.dateOfBirth}
                />
              )}
            </div>

            {isMinor && (
              <div role="group" aria-label={t('members.addMemberGuardianTitle')}>
                <p className="mb-2 text-sm font-medium">{t('members.addMemberGuardianTitle')}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label={t('members.detailsFirstName')}
                    value={guardianFirstName}
                    onValueChange={setGuardianFirstName}
                    maxLength={100}
                    isInvalid={!!errors.guardianFirstName}
                    errorMessage={errors.guardianFirstName}
                  />
                  <Input
                    label={t('members.detailsLastName')}
                    value={guardianLastName}
                    onValueChange={setGuardianLastName}
                    maxLength={100}
                    isInvalid={!!errors.guardianLastName}
                    errorMessage={errors.guardianLastName}
                  />
                </div>
              </div>
            )}

            {isMinor && owner && (
              <div
                role="status"
                className="flex flex-col gap-2 rounded-medium bg-primary/10 p-3 text-sm"
              >
                <p>
                  {owner.children.length
                    ? t('calendar.phoneBookingParentChildren', {
                        name: owner.member.name ?? '',
                      })
                    : t('calendar.phoneBookingParentFound', { name: owner.member.name ?? '' })}
                </p>
                {owner.children.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {owner.children.map((c) => {
                      const name = [c.first_name, c.last_name].filter(Boolean).join(' ')
                      return (
                        <Button
                          key={c.id}
                          size="sm"
                          variant="flat"
                          color="primary"
                          isDisabled={c.booked || saving}
                          onPress={() => void bookOnFile(owner.member.userId, c.id)}
                        >
                          {c.booked
                            ? t('calendar.phoneBookingChildBooked', { name })
                            : t('calendar.phoneBookingBookChild', { name })}
                        </Button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {existing && (
              <div
                role="alert"
                className="flex flex-col gap-2 rounded-medium bg-warning/10 p-3 text-sm"
              >
                <p>
                  {existing.name
                    ? t('calendar.phoneBookingExists', { name: existing.name })
                    : t('calendar.phoneBookingExistsNoName')}
                </p>
                <div>
                  <Button
                    size="sm"
                    color="warning"
                    variant="flat"
                    isLoading={saving}
                    onPress={() => void bookOnFile(existing.userId, null)}
                  >
                    {t('calendar.phoneBookingBookExisting')}
                  </Button>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose} isDisabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button color="primary" type="submit" isLoading={saving} isDisabled={!!existing}>
              {t('calendar.phoneBookingSave')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
